import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';
import { baseUrls } from '../../../constants/module-constants.js';
import { useAuthContext } from '../../../context/auth-context';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { StyledButton } from '../../../components/common/StyledButton';
import AffiliationFilter from '../../../components/reports/AffiliationFilter';
import { getAffiliationZones, filterByAffiliationZone } from '../../../utils/report-filters';
import { authFetch } from '../../../lib/http';

const GradesByCategoriesPage = () => {
  const { activeCamp } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [clubsData, setClubsData] = useState([]);
  const [rankingCategories, setRankingCategories] = useState([]);
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    if (!activeCamp?._id) {
      setClubsData([]);
      return;
    }
    fetchGrades(activeCamp._id);
  }, [activeCamp?._id]);

  // Al cambiar de campamento las zonas son otras — limpiamos el filtro
  useEffect(() => { setZoneFilter(''); }, [activeCamp?._id]);

  const zones = getAffiliationZones(clubsData);
  const visibleClubs = filterByAffiliationZone(clubsData, zoneFilter);

  const fetchGrades = async (campId) => {
    setLoading(true);
    try {
      const response = await authFetch(
        `${baseUrls.baseUrl}/reports/gradesByEventCategorySummary/${campId}`
      );
      const data = await response.json();
      setClubsData(data.results);
      if (data.rankingCategories?.length > 0) setRankingCategories(data.rankingCategories);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const FALLBACK_RANKING_CATEGORIES = [
    { name: '6 Estrellas', minPercentage: 90 },
    { name: '5 Estrellas', minPercentage: 79 },
    { name: '4 Estrellas', minPercentage: 69 },
    { name: '3 Estrellas', minPercentage: 59 },
    { name: '2 Estrellas', minPercentage: 49 },
    { name: '1 Estrella',  minPercentage: 0  },
  ];

  const getRanking = (percentDecimal) => {
    const pct = percentDecimal * 100;
    const categories = rankingCategories.length > 0 ? rankingCategories : FALLBACK_RANKING_CATEGORIES;
    const sorted = [...categories].sort((a, b) => b.minPercentage - a.minPercentage);
    for (const cat of sorted) {
      if (pct >= cat.minPercentage) return cat.name;
    }
    return sorted[sorted.length - 1]?.name ?? 'Indefinido';
  };

  const exportToPdf = async () => {
    const zip = new JSZip();
    const promises = [];

    visibleClubs.forEach((club) => {
      // Calculate camp sum
      let campSum = 0;
      club.eventCategories.forEach((category) => {
        category.events.forEach((event) => {
          campSum += event.maxPointEvents;
        });
      });

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      let yPosition = 10;

      // Club header
      doc.setFontSize(14);
      doc.text(`Club: ${club.clubName}`, 10, yPosition);
      yPosition += 7;
      doc.text(`Zona: ${club.affiliationZone}`, 10, yPosition);
      yPosition += 10;

      // Prepare table data
      const tableData = [];
      club.eventCategories.forEach((category) => {
        let categorySum = 0;
        category.events.forEach((event, eventIndex) => {
          categorySum += event.maxPointEvents;
          tableData.push([
            eventIndex === 0 ? category.categoryName : '',
            event.name,
            event.maxPointEvents,
            event.sumGrade,
            `${Math.ceil((event.sumGrade / event.maxPointEvents) * 100)}%`
          ]);
        });
        
        // Add category total row
        tableData.push([
          { content: 'Total', colSpan: 1, styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: `Total de Eventos: ${category.events.length}`, styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: categorySum.toString(), styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: category.categoryTotal.toString(), styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: `${Math.ceil((category.categoryTotal / categorySum) * 100)}%`, styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } }
        ]);
      });

      // Generate table
      autoTable(doc, {
        startY: yPosition,
        head: [['Renglones', 'Eventos', 'Valor', 'Puntos', '%']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: 'bold' },
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Summary section
      doc.setFontSize(10);
      const summaryY = yPosition;
      doc.text(`Categoría: ${getRanking(club.total / campSum)}`, 10, summaryY);
      doc.text(`Puntos Máximos: ${campSum}`, 70, summaryY);
      doc.text(`Total de Puntos: ${club.total}`, 126, summaryY);
      doc.text(`Porcentaje: ${Math.ceil((club.total / campSum) * 100)}%`, 170, summaryY);

      // Create filename and add to zip
      const fileName = `${club.affiliationZone}_${club.clubName}.pdf`;
      const pdfBlob = doc.output('blob');
      promises.push(zip.file(fileName, pdfBlob));
    });

    // Wait for all files to be added to the zip
    await Promise.all(promises);

    // Generate and download the zip file
    zip.generateAsync({ type: "blob" }).then(function (blob) {
      saveAs(blob, "resumen-categorias-evento.zip");
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Resumen por Categorías de Evento</Typography>
        <StyledButton
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportToPdf}
        >
          Generar PDF
        </StyledButton>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <AffiliationFilter value={zoneFilter} onChange={setZoneFilter} zones={zones} />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Typography variant="body2" color="text.secondary">
            {visibleClubs.length} de {clubsData.length} clubes
            {zoneFilter ? ` · ${zoneFilter}` : ''}
          </Typography>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        visibleClubs.map((club, clubIndex) => {
          let campSum = 0;
          club.eventCategories.forEach((category) => {
            category.events.forEach((event) => {
              campSum += event.maxPointEvents;
            });
          });

          return (
            <Card key={clubIndex} sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  <strong>Club:</strong> {club.clubName}
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
                  <strong>Zona:</strong> {club.affiliationZone}
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Renglones</strong></TableCell>
                        <TableCell><strong>Eventos</strong></TableCell>
                        <TableCell><strong>Valor</strong></TableCell>
                        <TableCell><strong>Puntos</strong></TableCell>
                        <TableCell><strong>%</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {club.eventCategories.map((category, categoryIndex) => {
                        let categorySum = 0;
                        return (
                          <React.Fragment key={categoryIndex}>
                            {category.events.map((event, eventIndex) => {
                              categorySum += event.maxPointEvents;
                              return (
                                <TableRow key={`${categoryIndex}-${eventIndex}`}>
                                  {eventIndex === 0 && (
                                    <TableCell rowSpan={category.events.length}>
                                      {category.categoryName}
                                    </TableCell>
                                  )}
                                  <TableCell>{event.name}</TableCell>
                                  <TableCell>{event.maxPointEvents}</TableCell>
                                  <TableCell>{event.sumGrade}</TableCell>
                                  <TableCell>
                                    {Math.ceil((event.sumGrade / event.maxPointEvents) * 100)}%
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow sx={{ backgroundColor: '#f1f1f1', fontWeight: 'bold' }}>
                              <TableCell><strong>Total</strong></TableCell>
                              <TableCell>
                                <strong>Total de Eventos: {category.events.length}</strong>
                              </TableCell>
                              <TableCell><strong>{categorySum}</strong></TableCell>
                              <TableCell><strong>{category.categoryTotal}</strong></TableCell>
                              <TableCell>
                                <strong>
                                  {Math.ceil((category.categoryTotal / categorySum) * 100)}%
                                </strong>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Categoría
                      </Typography>
                      <Typography variant="h6">
                        {getRanking(club.total / campSum)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Puntos Máximos
                      </Typography>
                      <Typography variant="h6">{campSum}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Total de Puntos
                      </Typography>
                      <Typography variant="h6">{club.total}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Porcentaje
                      </Typography>
                      <Typography variant="h6">
                        {Math.ceil((club.total / campSum) * 100)}%
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })
      )}
    </Container>
  );
};

export default GradesByCategoriesPage;